// Settings added in Phase 4: model policy, agent tokens (GPU worker / OpenClaw), OpenClaw wake-up,
// weekly reports, and the Mirage profile that competitors are compared with.
import { useEffect, useState } from 'react';
import { Empty, ErrorMsg, Section, Time } from '../components/ui';
import { api, errorText } from '../lib/api';
import type { AgentTokenRow, MirageProfile } from '../lib/deepTypes';
import { useApi, useSession } from '../lib/session';
import type { SettingsValues } from '../lib/types';

function useSave(key: keyof SettingsValues) {
  const { reloadSettings } = useSession();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [ok, setOk] = useState('');
  async function save(value: unknown) {
    setBusy(true);
    setError('');
    setOk('');
    try {
      await api(`/api/settings/${key}`, { method: 'PUT', body: { value } });
      setOk('Saved.');
      await reloadSettings();
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  }
  return { busy, error, ok, save };
}

function SaveRow({ dirty, busy, ok, label = 'Save' }: { dirty: boolean; busy: boolean; ok: string; label?: string }) {
  return (
    <div className="span2 row gap center">
      <button type="submit" className="btn primary small" disabled={!dirty || busy}>
        {label}
      </button>
      {ok && !dirty && <span className="small ok-text">{ok}</span>}
    </div>
  );
}

type ClaudeModel = { id: string; label: string };

// A preset list plus "other…" (any Claude model id or Claude Code alias, checked by the server).
function ClaudeModelPicker({ label, value, models, inherit, onChange }: {
  label: string;
  value: string | null;
  models: ClaudeModel[];
  inherit?: string;
  onChange: (v: string | null) => void;
}) {
  const known = value === null || models.some((m) => m.id === value);
  const [other, setOther] = useState(!known);
  return (
    <label className="field">
      <span>{label}</span>
      <select
        value={other ? '__other' : (value ?? '')}
        onChange={(e) => {
          const v = e.target.value;
          setOther(v === '__other');
          if (v !== '__other') onChange(v || null);
        }}
      >
        <option value="">{inherit ?? 'choose a model'}</option>
        {models.map((m) => (
          <option key={m.id} value={m.id}>
            {m.label} ({m.id})
          </option>
        ))}
        <option value="__other">other model id…</option>
      </select>
      {other && <input className="mono" aria-label={`${label}, model id`} value={value ?? ''} placeholder="claude-…" onChange={(e) => onChange(e.target.value.trim() || null)} />}
    </label>
  );
}

export function ModelPolicyForm({ value, note, models, tasks }: { value: SettingsValues['model_policy']; note: string; models: ClaudeModel[]; tasks: string[] }) {
  const [p, setP] = useState(value);
  const { busy, error, ok, save } = useSave('model_policy');
  useEffect(() => setP(value), [value]);
  const dirty = JSON.stringify(p) !== JSON.stringify(value);
  return (
    <Section title="Model policy" id="model-policy">
      <form
        className="form-grid"
        aria-label="Model policy"
        onSubmit={(e) => {
          e.preventDefault();
          void save(p);
        }}
      >
        <label className="field">
          <span>Mode</span>
          <select value={p.mode} onChange={(e) => setP({ ...p, mode: e.target.value as 'local_only' | 'cloud_allowed' })}>
            <option value="local_only">local only (no model call leaves your machines)</option>
            <option value="cloud_allowed">cloud allowed for chosen tasks</option>
          </select>
        </label>
        <label className="field">
          <span>Local provider</span>
          <select value={p.local.provider} onChange={(e) => setP({ ...p, local: { ...p.local, provider: e.target.value } })}>
            <option value="none">none (model jobs are not queued)</option>
            <option value="ollama">Ollama on the GPU worker</option>
          </select>
        </label>
        <label className="field">
          <span>Local model (as the GPU worker runs it)</span>
          <input value={p.local.model ?? ''} placeholder="qwen2.5:3b" onChange={(e) => setP({ ...p, local: { ...p.local, model: e.target.value || null } })} />
        </label>
        <label className="field">
          <span>Local model URL (on the GPU machine)</span>
          <input className="mono" value={p.local.base_url ?? ''} placeholder="http://127.0.0.1:41323" onChange={(e) => setP({ ...p, local: { ...p.local, base_url: e.target.value || null } })} />
        </label>
        <label className="check block span2">
          <input type="checkbox" checked={p.cloud.enabled} onChange={(e) => setP({ ...p, cloud: { ...p.cloud, enabled: e.target.checked } })} /> Cloud enabled (also
          needs mode “cloud allowed” and the task ticked below; quotations never go to a cloud model)
        </label>
        <label className="field">
          <span>Cloud provider</span>
          <select value={p.cloud.provider ?? ''} onChange={(e) => setP({ ...p, cloud: { ...p.cloud, provider: e.target.value || null } })}>
            <option value="">none</option>
            <option value="claude_agent_sdk">Claude, through Claude Code on the agent machine</option>
            <option value="openai_compatible">OpenAI-compatible (set in the agent’s environment)</option>
          </select>
        </label>
        {p.cloud.provider === 'claude_agent_sdk' ? (
          <ClaudeModelPicker label="Default Claude model" value={p.cloud.model} models={models} onChange={(m) => setP({ ...p, cloud: { ...p.cloud, model: m } })} />
        ) : (
          <div />
        )}
        {tasks.map((t) => (
          <div key={t} className="span2 row gap center wrap">
            <label className="check block">
              <input
                type="checkbox"
                checked={p.cloud.allowed_tasks.includes(t)}
                onChange={(e) =>
                  setP({ ...p, cloud: { ...p.cloud, allowed_tasks: e.target.checked ? [...p.cloud.allowed_tasks, t] : p.cloud.allowed_tasks.filter((x) => x !== t) } })
                }
              />{' '}
              run {t.replaceAll('_', ' ')} on the cloud model
            </label>
            {p.cloud.provider === 'claude_agent_sdk' && p.cloud.allowed_tasks.includes(t) && (
              <ClaudeModelPicker
                label={`Model for ${t.replaceAll('_', ' ')}`}
                value={p.cloud.task_models[t] ?? null}
                models={models}
                inherit={p.cloud.model ? `default (${p.cloud.model})` : 'default (none chosen)'}
                onChange={(m) => {
                  const tm = { ...p.cloud.task_models };
                  if (m) tm[t] = m;
                  else delete tm[t];
                  setP({ ...p, cloud: { ...p.cloud, task_models: tm } });
                }}
              />
            )}
          </div>
        ))}
        <SaveRow dirty={dirty} busy={busy} ok={ok} label="Save model policy" />
      </form>
      <ErrorMsg>{error}</ErrorMsg>
      <p className="msg info small">
        {note} A task ticked for the cloud runs on the cloud model; every other task runs locally. A failure is reported as a failure: it never falls back to
        another model. Each result records which model produced it.
      </p>
    </Section>
  );
}

export function AgentTokens() {
  const list = useApi<{ items: AgentTokenRow[]; job_types: string[] }>('/api/agent-tokens');
  const [name, setName] = useState('');
  const [types, setTypes] = useState<string[]>([]);
  const [fresh, setFresh] = useState<AgentTokenRow | null>(null);
  const [err, setErr] = useState('');

  async function create(e: React.FormEvent) {
    e.preventDefault();
    setErr('');
    try {
      const t = await api<AgentTokenRow>('/api/agent-tokens', { body: { name, job_types: types } });
      setFresh(t);
      setName('');
      list.reload();
    } catch (e2) {
      setErr(errorText(e2));
    }
  }
  async function revoke(id: string) {
    try {
      await api(`/api/agent-tokens/${id}/revoke`, { method: 'POST' });
      list.reload();
    } catch (e) {
      setErr(errorText(e));
    }
  }
  const jobTypes = list.data?.job_types ?? [];
  return (
    <Section title="Agents (GPU worker, OpenClaw)" id="agents">
      <p className="small">
        An agent claims model jobs over HTTPS with its own token and returns results; it never touches the database. A token may only take the job
        types ticked for it. Quotation work is never given to an agent.
      </p>
      {list.data && list.data.items.length === 0 && <Empty>No agent token yet.</Empty>}
      {list.data && list.data.items.length > 0 && (
        <ul className="plain small">
          {list.data.items.map((t) => (
            <li key={t.id}>
              <strong>{t.name}</strong> · {t.job_types.join(', ')} · last used {t.last_used_at ? <Time iso={t.last_used_at} /> : 'never'}
              {t.revoked_at ? (
                <span className="muted"> · revoked</span>
              ) : (
                <>
                  {' '}
                  <button type="button" className="btn small" onClick={() => void revoke(t.id)}>
                    Revoke
                  </button>
                </>
              )}
            </li>
          ))}
        </ul>
      )}
      <form className="form-grid" aria-label="New agent token" onSubmit={create}>
        <label className="field">
          <span>Name</span>
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder="gpu-worker" />
        </label>
        <fieldset className="field">
          <legend>May claim</legend>
          {jobTypes.map((t) => (
            <label key={t} className="check block">
              <input type="checkbox" checked={types.includes(t)} onChange={(e) => setTypes(e.target.checked ? [...types, t] : types.filter((x) => x !== t))} />{' '}
              {t.replaceAll('_', ' ')}
            </label>
          ))}
        </fieldset>
        <div className="span2">
          <button type="submit" className="btn small" disabled={name.trim().length < 2 || types.length === 0}>
            Create token
          </button>
        </div>
      </form>
      {fresh?.token && (
        <div className="msg warn small">
          Token for <strong>{fresh.name}</strong>, shown once: <code className="mono">{fresh.token}</code>
          <br />
          Put it in the agent’s environment as MIRAGE_AGENT_TOKEN now; it cannot be shown again.
        </div>
      )}
      <ErrorMsg>{err || list.error}</ErrorMsg>
    </Section>
  );
}

export function OpenClawForm({ value }: { value: SettingsValues['openclaw'] }) {
  const [o, setO] = useState(value);
  const { busy, error, ok, save } = useSave('openclaw');
  useEffect(() => setO(value), [value]);
  const dirty = JSON.stringify(o) !== JSON.stringify(value);
  return (
    <Section title="OpenClaw (optional)">
      <form
        className="form-grid"
        aria-label="OpenClaw"
        onSubmit={(e) => {
          e.preventDefault();
          void save(o);
        }}
      >
        <label className="check block span2">
          <input type="checkbox" checked={o.enabled} onChange={(e) => setO({ ...o, enabled: e.target.checked })} /> Wake OpenClaw when a model job is
          queued
        </label>
        <label className="field">
          <span>Gateway hooks URL</span>
          <input className="mono" value={o.hooks_url ?? ''} placeholder="http://127.0.0.1:18789/hooks" onChange={(e) => setO({ ...o, hooks_url: e.target.value || null })} />
        </label>
        <label className="field">
          <span>Agent id</span>
          <input value={o.agent_id} onChange={(e) => setO({ ...o, agent_id: e.target.value })} />
        </label>
        <SaveRow dirty={dirty} busy={busy} ok={ok} />
      </form>
      <ErrorMsg>{error}</ErrorMsg>
      <p className="muted small">
        The app keeps every schedule; OpenClaw only executes. The hook token lives in the server environment (OPENCLAW_HOOK_TOKEN), and the wake
        message is fixed text: no page content is sent. OpenClaw then claims work with an agent token like the GPU worker
        (integrations/openclaw/ in the repository).
      </p>
    </Section>
  );
}

const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

export function ReportsForm({ value }: { value: SettingsValues['reports'] }) {
  const [r, setR] = useState(value);
  const { busy, error, ok, save } = useSave('reports');
  useEffect(() => setR(value), [value]);
  const dirty = JSON.stringify(r) !== JSON.stringify(value);
  return (
    <Section title="Weekly report">
      <form
        className="form-grid"
        aria-label="Weekly report"
        onSubmit={(e) => {
          e.preventDefault();
          void save(r);
        }}
      >
        <label className="check block span2">
          <input type="checkbox" checked={r.weekly_enabled} onChange={(e) => setR({ ...r, weekly_enabled: e.target.checked })} /> Compile every week
        </label>
        <label className="field">
          <span>Day</span>
          <select value={r.weekday} onChange={(e) => setR({ ...r, weekday: Number(e.target.value) })}>
            {DAYS.map((d, i) => (
              <option key={d} value={i}>
                {d}
              </option>
            ))}
          </select>
        </label>
        <label className="field">
          <span>Time (reporting time zone)</span>
          <input type="time" value={r.time} onChange={(e) => setR({ ...r, time: e.target.value })} />
        </label>
        <label className="check block span2">
          <input type="checkbox" checked={r.suggest_actions} onChange={(e) => setR({ ...r, suggest_actions: e.target.checked })} /> Ask the model agent for
          three suggested actions (needs a local provider)
        </label>
        <SaveRow dirty={dirty} busy={busy} ok={ok} />
      </form>
      <ErrorMsg>{error}</ErrorMsg>
    </Section>
  );
}

function ListInput({ label, value, onChange, options }: { label: string; value: string[]; onChange: (v: string[]) => void; options?: string[] }) {
  if (options) {
    return (
      <fieldset className="field">
        <legend>{label}</legend>
        {options.map((o) => (
          <label key={o} className="check block">
            <input type="checkbox" checked={value.includes(o)} onChange={(e) => onChange(e.target.checked ? [...value, o] : value.filter((x) => x !== o))} /> {o}
          </label>
        ))}
      </fieldset>
    );
  }
  return (
    <label className="field">
      <span>{label}</span>
      <input
        value={value.join(', ')}
        onChange={(e) =>
          onChange(
            e.target.value
              .split(',')
              .map((x) => x.trim().toUpperCase())
              .filter(Boolean),
          )
        }
      />
    </label>
  );
}

export function MirageProfileForm({ value, services, clients }: { value: MirageProfile; services: string[]; clients: string[] }) {
  const [m, setM] = useState(value);
  const { busy, error, ok, save } = useSave('mirage_profile');
  useEffect(() => setM(value), [value]);
  const dirty = JSON.stringify(m) !== JSON.stringify(value);
  return (
    <Section title="Mirage profile (for comparisons)" id="mirage-profile">
      <form
        className="form-grid"
        aria-label="Mirage profile"
        onSubmit={(e) => {
          e.preventDefault();
          void save(m);
        }}
      >
        <label className="field span2">
          <span>Headline (Mirage’s own positioning sentence)</span>
          <input value={m.headline ?? ''} onChange={(e) => setM({ ...m, headline: e.target.value || null })} />
        </label>
        <ListInput label="Services" value={m.services} onChange={(v) => setM({ ...m, services: v })} options={services} />
        <ListInput label="Client types" value={m.client_types} onChange={(v) => setM({ ...m, client_types: v })} options={clients} />
        <ListInput label="Markets (ISO country codes, comma-separated)" value={m.markets} onChange={(v) => setM({ ...m, markets: v })} />
        <SaveRow dirty={dirty} busy={busy} ok={ok} />
      </form>
      <ErrorMsg>{error}</ErrorMsg>
      <p className="muted small">Studios are compared with this profile side by side, with no score. An empty list shows as “Mirage profile has none yet”.</p>
    </Section>
  );
}
